/**
 * /platform — the hub / overview page listing every tool with a card each.
 *
 * Different from the individual feature pages: single page, brief blurb per tool,
 * strong SEO keywords aggregate, one hero + one CTA at the bottom.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Bot, Calculator, ChefHat, Video, Users, Building2,
  ArrowRight, Sparkles, LogIn, UserPlus, LayoutGrid, ChevronRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import BrandMark from '../../components/BrandMark';
import ThemeToggle from '../../components/ThemeToggle';
import PageHero from '../../components/PageHero';
import useDocumentMeta from '../../hooks/useDocumentMeta';

const TOOLS = [
  {
    slug: 'learning', icon: BookOpen, title: 'Learning & Courses',
    tagline: 'Structured lessons written by working operators.',
    bullets: ['Self-paced video + text', 'Free foundations track', 'Quizzes, checklists + badges'],
  },
  {
    slug: 'ai-assistant', icon: Bot, title: 'AI Assistant',
    tagline: 'A freeze-dry assistant that actually knows freeze-drying.',
    bullets: ['Cited answers, not hallucinations', 'Multi-turn memory', 'Cycle troubleshooting'],
  },
  {
    slug: 'business-tools', icon: Calculator, title: 'Business Tools',
    tagline: 'Real batch math — margin, break-even, sensitivity, pro-forma.',
    bullets: ['Unit + batch economics', 'Full pro-forma P&L', 'Sensitivity tornado charts'],
  },
  {
    slug: 'calculators', icon: ChefHat, title: 'Calculators',
    tagline: 'Cost, energy, yield and shelf-life for hobby batches.',
    bullets: ['Multi-item batches', 'Energy math', 'Yield + shelf-life estimator'],
  },
  {
    slug: 'videos', icon: Video, title: 'Video Library',
    tagline: 'Curated tutorials + deep-dives in one place.',
    bullets: ['Filter by topic + machine', 'Community-rated', 'Fresh videos weekly'],
  },
  {
    slug: 'community', icon: Users, title: 'Community Forum',
    tagline: 'Talk shop with operators running the same gear.',
    bullets: ['Threaded discussions', 'Reply notifications', 'Actively moderated'],
  },
  {
    slug: 'directory', icon: Building2, title: 'Vendor Directory',
    tagline: 'The vendors + consultants we actually trust.',
    bullets: ['Vetted, not open', 'Real operator reviews', 'Region + service filters'],
  },
];

const PlatformHubPage = () => {
  const navigate = useNavigate();

  useDocumentMeta({
    title: 'Platform — Freeze Dry Academy',
    description: 'Every tool in the Freeze Dry Academy platform: Learning & Courses, AI Assistant, Business Tools, Calculators, Video Library, Community Forum and Vendor Directory.',
    keywords: 'freeze dry academy, freeze drying tools, freeze dry business platform, freeze dryer community',
  });

  return (
    <div className="min-h-screen bg-background" data-testid="platform-hub-page">
      {/* Top nav */}
      <header className="border-b border-border">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark size="md" subtitle="Platform" onClick={() => navigate('/')} />
          <nav className="hidden md:flex items-center gap-6 text-sm font-semibold text-foreground/80">
            <button onClick={() => navigate('/platform')} className="text-foreground">Platform</button>
            <button onClick={() => navigate('/directory')} className="hover:text-foreground transition">Directory</button>
            <button onClick={() => navigate('/pricing')} className="hover:text-foreground transition">Pricing</button>
            <button onClick={() => navigate('/consulting')} className="hover:text-foreground transition">Consulting</button>
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={() => navigate('/login')} className="rounded-full font-semibold hidden sm:inline-flex" data-testid="hub-signin">
              <LogIn className="w-3.5 h-3.5 mr-1.5" /> Sign in
            </Button>
            <Button size="sm" onClick={() => navigate('/login?mode=signup')} className="rounded-full font-semibold" data-testid="hub-signup">
              <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Sign up
            </Button>
          </div>
        </div>
      </header>

      <PageHero
        eyebrow="Platform"
        eyebrowIcon={LayoutGrid}
        title={<>One platform. <span className="text-amber-brand">Every freeze-dry tool.</span></>}
        description="Learning, AI, calculators, video, community and vendor discovery — designed like the machines they teach: durable, simple, and built for daily use."
        backTo="/"
        backLabel="Home"
        testId="platform-hub-hero"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button size="lg" onClick={() => navigate('/login?mode=signup')} className="rounded-full font-extrabold px-6" data-testid="hub-hero-primary">
              Get started free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/pricing')}
              className="rounded-full font-extrabold px-6 bg-background/10 border-background/30 text-background hover:bg-background/20 hover:text-background"
              data-testid="hub-hero-secondary"
            >
              See pricing
            </Button>
          </div>
        }
      />

      {/* Tool grid */}
      <section className="container mx-auto px-6 py-20" data-testid="platform-hub-grid">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {TOOLS.map(({ slug, icon: Ic, title, tagline, bullets }, i) => (
            <button
              key={slug}
              onClick={() => navigate(`/platform/${slug}`)}
              className="pro-card-interactive text-left p-6 group animate-fade-up"
              style={{ '--i': i }}
              data-testid={`hub-tool-${slug}`}
            >
              <div className="flex items-start justify-between">
                <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Ic className="w-5 h-5" />
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition" />
              </div>
              <h3 className="mt-5 font-heading text-xl font-bold tracking-tight">{title}</h3>
              <p className="mt-1.5 text-muted-foreground text-[14px] leading-relaxed">{tagline}</p>
              <ul className="mt-4 space-y-1.5">
                {bullets.map((b, j) => (
                  <li key={j} className="text-xs text-muted-foreground flex items-start gap-2">
                    <span className="mt-1 h-1 w-1 rounded-full bg-primary flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 text-xs font-extrabold text-primary uppercase tracking-widest flex items-center gap-1 group-hover:gap-2 transition-all">
                Learn more <ArrowRight className="w-3 h-3" />
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-navy-spotlight text-background" data-testid="platform-hub-cta">
        <div className="container mx-auto px-6 py-20 text-center max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-background/20 bg-background/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-background/70">
            <Sparkles className="w-3 h-3" /> Free forever tier
          </div>
          <h2 className="mt-5 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">
            One account. <span className="text-amber-brand">Every tool unlocked.</span>
          </h2>
          <p className="mt-4 text-background/70 text-base sm:text-lg leading-relaxed">
            The Foundations track, household calculator, video library and community forum are all free — forever.
            Upgrade to Pro when you\u2019re ready to run this as a business.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button size="lg" onClick={() => navigate('/login?mode=signup')} className="rounded-full font-extrabold px-6 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="hub-final-signup">
              Sign up free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/pricing')}
              className="rounded-full font-extrabold px-6 bg-background/10 border-background/30 text-background hover:bg-background/20 hover:text-background"
              data-testid="hub-final-pricing"
            >
              Compare tiers
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border bg-background">
        <div className="container mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-3">
            <BrandMark size="sm" onClick={() => navigate('/')} />
            <span>© {new Date().getFullYear()} Freeze Dry Academy · Peak and Prairie Industries Corp.</span>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <button onClick={() => navigate('/platform')} className="hover:text-foreground transition">Platform</button>
            <button onClick={() => navigate('/pricing')} className="hover:text-foreground transition">Pricing</button>
            <button onClick={() => navigate('/directory')} className="hover:text-foreground transition">Directory</button>
            <button onClick={() => navigate('/consulting')} className="hover:text-foreground transition">Consulting</button>
            <button onClick={() => navigate('/login')} className="hover:text-foreground transition">Sign in</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PlatformHubPage;
